import { Instance, applySnapshot, types, flow, onSnapshot, getEnv } from "mobx-state-tree";
import { RootStoreBase } from "./RootStore.base";
import { CompanyModel, IndustryModel, FundModel, UserModel, TrendModel, FundingRoundModelType } from ".";
import _ from "lodash";
import { SESSION_TOKEN_KEY } from "../utils/variables";
import { toast } from "react-toastify";
import ReactGA from "react-ga";

export interface RootStoreType extends Instance<typeof RootStore.Type> {}

export const RootStore = RootStoreBase.props({
  /**
   * Available funds for the user to choose
   */
  funds: types.array(FundModel),
  /**
   * loader for fund query
   */
  fundsLoading: true,
  /**
   * error field for fund query
   */
  fundsError: "",
  /**
   * User's object. Contains important info, such as role.
   */
  user: UserModel,
  /**
   * error field for login
   */
  loginError: "",
  /**
   * Companies listed on the portfolio section
   */
  companies: types.array(CompanyModel),
  companiesLoading: false,
  companiesError: "",
  /**
   * Industrys listed on the research section
   */
  industrys: types.array(IndustryModel),
  industrysLoading: true,
  industrysError: "",
  /**
   * Trends listed on the trends section
   */
  trends: types.array(TrendModel),
  trendsLoading: true,
  trendsError: "",
  /**
   * Loading while fetching funding rounds for company
   */
  companyFundsLoading: false,
  companyFundsError: "",
  showDisclaimerModal: true,
})
  .actions((self) => {
    // This is an auto-generated example action.
    async function log() {
      console.log(JSON.stringify(self));
    }
    // @ts-ignore
    const fetchFunds = flow(function* fetchFunds() {
      try {
        applySnapshot(self, { ...self, fundsError: "", fundsLoading: true });
        const response = yield self.queryCustomFetchFunds({}, undefined, { fetchPolicy: "network-only" });
        console.log("fetch funds response: ", response);
        const funds = response.customFetchFunds
          .sort((a, b) => a.id - b.id)
          .map((f, i) => {
            return { ...f, selected: i === 0 };
          });

        applySnapshot(self, {
          ...self,
          funds: funds.map((f) => f.id).filter((f) => f === 12).length // show "Base10 Partners Fund I" by default
            ? funds.map((f) => ({ ...f, selected: f.id === 12 }))
            : funds,
          fundsError: "",
          fundsLoading: false,
        });
      } catch (e) {
        console.log("failed to apply snapshot: ", e);
        applySnapshot(self, {
          ...self,
          fundsError: "Error fetching funds",
          fundsLoading: false,
        });
      }
    });
    // @ts-ignore
    const fetchUser = flow(function* fetchUser() {
      try {
        // TODO: Replace zero with id property
        const { user } = yield self.queryUser({ where: { id: 0 } });
        applySnapshot(self, { ...self, user });
      } catch (e) {
        console.log("failed to apply snapshot: ", e);
      }
    });
    // @ts-ignore
    const signup = flow(function* signup({
      username,
      email,
      password,
    }: {
      username: string;
      email: string;
      password: string;
    }) {
      try {
        const data = yield self.mutateRegister(
          { user: { username, email, password } },
          (qb) =>
            qb.email.id.role.session_token.name.username.accessible_funds.hbcu_data_room_access.accessible_letters,
        );

        getEnv(self).gqlHttpClient.setHeaders({ authorization: data.register.session_token });

        localStorage.setItem(SESSION_TOKEN_KEY, data.register.session_token);

        applySnapshot(self, {
          ...self,
          user: {
            name: data.register.name,
            username: data.register.username,
            session_token: data.register.session_token,
            role: data.register.role,
            id: data.register.id,
            email: data.register.email,
            accessible_funds: [...data.login.accessible_funds],
            accessible_letters: [...data.login.accessible_letters],
            hbcu_data_room_access: data.register.hbcu_data_room_access,
          },
        });

        yield fetchFunds();
        return { success: true, message: "" };
      } catch (error) {
        console.log("error", error);
        const response = error.response;
        return {
          success: false,
          message: response && response.errors && response.errors.length ? response.errors[0].message : "",
        };
      }
    });
    // @ts-ignore
    const resetPassword = flow(function* resetPassword({ username }: { username: string; email: string }) {
      let options;
      if (username.includes("@")) {
        options = { email: username };
      } else {
        options = { username };
      }
      try {
        const data = yield self.mutateForgotPassword(options);

        return { success: true, message: "" };
      } catch (error) {
        console.log("error", error);
        const response = error.response;
        return {
          success: false,
          message: response && response.errors && response.errors.length ? response.errors[0].message : "",
        };
      }
    });
    // @ts-ignore
    const newPassword = flow(function* newPassword({ username, password }: { username: string; password: string }) {
      let options;
      if (username.includes("@")) {
        options = { email: username, password };
      } else {
        options = { username, password };
      }
      try {
        const data = yield self.mutateResetPassword(options, (qb) => qb.email.id.role.session_token.name.username);
        console.log("data: ", data);
        // localStorage.setItem(SESSION_TOKEN_KEY, data.resetPassword.session_token);
        // getEnv(self).gqlHttpClient.setHeaders({ authorization: data.resetPassword.session_token });
        // applySnapshot(self, {
        //   ...self,
        //   user: {
        //     name: data.resetPassword.name,
        //     username: data.resetPassword.username,
        //     session_token: data.resetPassword.session_token,
        //     role: data.resetPassword.role,
        //     id: data.resetPassword.id,
        //     email: data.resetPassword.email,
        //   },
        // });
        // yield fetchFunds();
        return { success: true, message: "" };
      } catch (error) {
        console.log("error", error);
        const response = error.response;
        return {
          success: false,
          message: response && response.errors && response.errors.length ? response.errors[0].message : "",
        };
      }
    });
    // @ts-ignore
    const login = flow(function* login({
      username: username_raw,
      password,
      token,
    }: {
      username: string;
      password: string;
      token?: string;
    }) {
      try {
        const username = username_raw.toLowerCase();
        let options;
        if (username.includes("@")) {
          options = { email: username, password, token };
        } else {
          options = { username, password, token };
        }
        const data = yield self.mutateLogin(
          { user: options },
          (qb) =>
            qb.email.id.role.session_token.name.username.accessible_funds.hbcu_data_room_access.accessible_letters,
        );

        console.log("data: ", data.login.accessible_funds);

        localStorage.setItem(SESSION_TOKEN_KEY, data.login.session_token);

        getEnv(self).gqlHttpClient.setHeaders({ authorization: data.login.session_token });

        applySnapshot(self, {
          ...self,
          user: {
            name: data.login.name,
            username: data.login.username,
            session_token: data.login.session_token,
            role: data.login.role,
            id: data.login.id,
            email: data.login.email,
            accessible_funds: [...data.login.accessible_funds],
            accessible_letters: [...data.login.accessible_letters],
            hbcu_data_room_access: data.login.hbcu_data_room_access,
          },
        });
        yield fetchFunds();
        return { success: true, message: "" };
      } catch (error) {
        console.log("error", error);
        const response = error.response;
        return {
          success: false,
          message: response && response.errors && response.errors.length ? response.errors[0].message : "",
        };
      }
    });
    // @ts-ignore
    function logout() {
      applySnapshot(self, {
        ...self,
        ...defaultRootStoreValues,
      });

      getEnv(self).gqlHttpClient.setHeaders({ authorization: "" });
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
    // @ts-ignore
    function modalShown() {
      applySnapshot(self, {
        ...self,
        showDisclaimerModal: false,
      });
    }
    const afterCreate = flow(function* afterCreate() {
      onSnapshot(self, (snapshot) => {
        console.log("new snapshot: ", snapshot);
      });
      const session_token = localStorage.getItem(SESSION_TOKEN_KEY);
      if (session_token) {
        console.log("token true");
        getEnv(self).gqlHttpClient.setHeaders({ authorization: session_token });
        yield fetchFunds();
      } else {
        logout();
      }
    });

    const refetchFunds = flow(function* refetchFunds() {
      yield fetchFunds();
    });
    function localAddFund({ id, name }: { id: number; name: string }) {
      applySnapshot(self, {
        ...self,
        funds: [...self.funds, { id, name, selected: false }],
      });
    }
    function localAddIndustry({
      id,
      name,
      final_dilligence_count,
      ordinal,
    }: {
      id: number;
      name: string;
      final_dilligence_count: number;
      ordinal: number;
    }) {
      applySnapshot(self, {
        ...self,
        industrys: [...self.industrys, { id, name, final_dilligence_count, ordinal }],
      });
    }
    function localUpdateIndustry(params: {
      id: number;
      name: string;
      final_dilligence_count: number;
      ordinal: number;
    }) {
      applySnapshot(self, {
        ...self,
        industrys: self.industrys.map((s) => {
          if (s.id === params.id) {
            return { ...s, ...params };
          }
          return { ...s };
        }),
      });
    }
    function localRemoveIndustry(id: number) {
      applySnapshot(self, {
        ...self,
        industrys: self.industrys.filter((s) => s.id !== id),
      });
    }
    function localAddTrend({
      id,
      name,
      final_dilligence_count,
      ordinal,
    }: {
      id: number;
      name: string;
      final_dilligence_count: number;
      ordinal: number;
    }) {
      applySnapshot(self, {
        ...self,
        trends: [...self.trends, { id, name, final_dilligence_count, ordinal }],
      });
    }
    function localUpdateTrend(params: { id: number; name: string; final_dilligence_count: number; ordinal: number }) {
      applySnapshot(self, {
        ...self,
        trends: self.trends.map((s) => {
          if (s.id === params.id) {
            return { ...s, ...params };
          }
          return { ...s };
        }),
      });
    }
    function localRemoveTrend(id: number) {
      applySnapshot(self, {
        ...self,
        trends: self.trends.filter((s) => s.id !== id),
      });
    }
    function localRemoveCompany(id: number) {
      console.log("remove company: ", id);
      applySnapshot(self, {
        ...self,
        companies: self.companies.filter((s) => s.id !== id),
      });
    }
    function localAddCompany({
      id,
      name,
      industry_string,
      ordinal,
      logo_url,
      num_funding_rounds,
      funding_rounds,
    }: {
      id: number;
      name: string;
      industry_string: string;
      ordinal: number;
      logo_url: string;
      num_funding_rounds: number;
      funding_rounds: FundingRoundModelType[];
    }) {
      applySnapshot(self, {
        ...self,
        companies: [
          ...self.companies,
          {
            id,
            name,
            industry_string,
            ordinal,
            logo_url,
            num_funding_rounds,
            funding_rounds: _.cloneDeep(funding_rounds),
          },
        ],
      });
    }
    function localUpdateCompany(params: { id: number; name: string; industry_string: string; ordinal: number }) {
      applySnapshot(self, {
        ...self,
        companies: self.companies.map((c) => {
          if (c.id === params.id) {
            return { ...c, ...params };
          }
          return { ...c };
        }),
      });
    }
    function selectFund(idParam: string) {
      const id = parseInt(idParam);

      const funds = self.funds.map((f) => {
        if (f.id === id) {
          return { ...f, selected: true };
        }
        return { ...f, selected: false };
      });
      applySnapshot(self, { ...self, funds });
    }
    function _selected_fund() {
      const selectedFunds = self.funds.filter((f) => f.selected);
      if (!selectedFunds.length) {
        return { name: "", id: 0 };
      }
      return selectedFunds[0];
    }
    // @ts-ignore
    const fetchCompanies = flow(function* fetchCompanies() {
      try {
        applySnapshot(self, {
          ...self,
          companiesError: "",
          companiesLoading: true,
        });

        const companies = yield self.queryFetchCompanies(
          { fundId: _selected_fund().id },
          (qb) =>
            qb.id.name.ordinal.num_funding_rounds.industry_text.governance_maturity_index.logo_url.funding_rounds(
              (b) =>
                b.base10_invest.coinvestors((b) => b.id.name.url).footnote.funding_date.id.ownership.stage
                  .total_investment.round_number.valuation,
            ),
          { fetchPolicy: "network-only" },
        );
        console.log("companies: ", companies.fetchCompanies);

        applySnapshot(self, {
          ...self,
          companies: companies.fetchCompanies,
          companiesError: "",
          companiesLoading: false,
        });
      } catch (e) {
        console.log("failed to apply snapshot: ", e);
        applySnapshot(self, {
          ...self,
          companiesError: "Error fetching company list",
          companiesLoading: false,
        });
      }
    });
    // @ts-ignore
    const fetchIndustrys = flow(function* fetchIndustrys() {
      try {
        applySnapshot(self, {
          ...self,
          industrysError: "",
          industrysLoading: true,
        });
        const data = yield self.queryFetchIndustries(
          { fundId: _selected_fund().id },
          (qb) => qb.id.name.final_dilligence_count.ordinal,
          { fetchPolicy: "network-only" },
        );
        applySnapshot(self, {
          ...self,
          industrys: data.fetchIndustries,
          industrysError: "",
          industrysLoading: false,
        });
      } catch (e) {
        console.log("failed to apply snapshot: ", e);
        applySnapshot(self, {
          ...self,
          industrysError: "Error fetching company list",
          industrysLoading: false,
        });
      }
    });
    // @ts-ignore
    const fetchTrends = flow(function* fetchTrends() {
      try {
        applySnapshot(self, {
          ...self,
          trendsError: "",
          trendsLoading: true,
        });
        const data = yield self.queryFetchTrends(
          { fundId: _selected_fund().id },
          (qb) => qb.id.name.final_dilligence_count.ordinal,
          { fetchPolicy: "network-only" },
        );
        console.log("data: ", data);
        applySnapshot(self, {
          ...self,
          trends: data.fetchTrends,
          trendsError: "",
          trendsLoading: false,
        });
      } catch (e) {
        console.log("failed to apply snapshot: ", e);
        applySnapshot(self, {
          ...self,
          trendsError: "Error fetching company list",
          trendsLoading: false,
        });
      }
    });
    // @ts-ignore
    const fetchCompanyFundingRounds = flow(function* fetchCompanyFundingRounds(id: number) {
      try {
        applySnapshot(self, {
          ...self,
          companyFundsError: "",
          companyFundsLoading: true,
        });
        const data = yield self.queryCompany(
          { where: { id } },
          (qb) =>
            qb.id.funding_rounds(
              (b) =>
                b.base10_invest.coinvestors((b) => b.id.name).footnote.funding_date.id.ownership.stage.total_investment,
            ),
          { fetchPolicy: "network-only" },
        );
        console.log("data fetch rounds: ", data);
        applySnapshot(self, {
          ...self,
          companyFundsError: "",
          companyFundsLoading: false,
          companies: self.companies.map((c) => {
            if (c.id === id) {
              return {
                ...c,
                ...data.company,
              };
            }
            return { ...c };
          }),
        });
      } catch (e) {
        console.log("error saving rounds to store: ", e);
        applySnapshot(self, {
          ...self,
          companyFundsError: "Error fetching funding rounds",
          companyFundsLoading: false,
        });
      }
    });
    function localRemoveFundingRound(id: number) {
      applySnapshot(self, {
        ...self,
        companies: self.companies.filter((s) => s.id !== id),
      });
    }
    return {
      log,
      fetchFunds,
      login,
      afterCreate,
      fetchUser,
      selectFund,
      refetchFunds,
      localAddFund,
      fetchCompanies,
      fetchIndustrys,
      localAddIndustry,
      localAddCompany,
      localRemoveCompany,
      localRemoveIndustry,
      localUpdateCompany,
      localUpdateIndustry,
      fetchCompanyFundingRounds,
      localRemoveFundingRound,
      localUpdateTrend,
      localAddTrend,
      localRemoveTrend,
      fetchTrends,
      logout,
      signup,
      resetPassword,
      newPassword,
      modalShown,
    };
  })
  .views((self) => ({
    get fund_list() {
      return (
        self.funds
          // TODO: Remove this line when going live with Advancement Initiative
          // .filter(({ id }) => [12, 13].includes(id))
          .map(({ id, name }) => ({
            value: `${id}`,
            label: name,
          }))
      );
    },
    get selected_fund() {
      const selectedFunds = self.funds.filter((f) => f.selected);
      if (!selectedFunds.length) {
        return {
          name: "",
          id: 0,
          total_investment_value: 0,
          total_company_value: 0,
        };
      }
      return selectedFunds[0];
    },
    get is_authenticated() {
      return !!self.user && !!self.user.session_token;
    },
    get company_max_ordinal() {
      return self.companies.map((c) => c.ordinal).reduce((a, b) => Math.max(a, b), 0);
    },
    get industry_max_ordinal() {
      return self.industrys.map((c) => c.ordinal).reduce((a, b) => Math.max(a, b), 0);
    },
    get trend_max_ordinal() {
      return self.trends.map((c) => c.ordinal).reduce((a, b) => Math.max(a, b), 0);
    },
  }));

export type RootStore = Instance<typeof RootStore>;

export const defaultRootStoreValues: any = {
  funds: [],
  user: {
    id: 0,
    name: "",
    email: "",
    password: "",
    role: "INVESTOR",
    session_token: "",
    url: "",
  },
  showDisclaimerModal: true,
};
